import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FileUp, Mic, Trash2, Volume2, ZoomIn, ZoomOut } from 'lucide-react';
import { clsx } from 'clsx';
import DocumentNarrationPlayer from './DocumentNarrationPlayer';

let pdfJsLoaderPromise = null;

const ensurePdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfJsLoaderPromise) return pdfJsLoaderPromise;

  pdfJsLoaderPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-pdfjs-loader="true"]');

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.pdfjsLib), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load PDF viewer.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    script.dataset.pdfjsLoader = 'true';
    script.onload = () => {
      if (!window.pdfjsLib) {
        reject(new Error('Failed to initialize PDF viewer.'));
        return;
      }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('Failed to load PDF viewer.'));
    document.body.appendChild(script);
  });

  return pdfJsLoaderPromise;
};

const PdfDocumentPanel = ({
  pdf,
  isUploading = false,
  error = '',
  canEdit = true,
  onUpload,
  onDelete,
  renderMode = 'canvas',
  audioPages = {},
  canManageAudio = false,
  onAddPageAudio,
  onMovePageAudio,
  onRemovePageAudio,
  onManagePageAudio,
}) => {
  const embedViewportHeight = 78;
  const documentBaseWidth = '210mm';
  const canvasContainerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const pdfDocRef = useRef(null);
  const canvasRefs = useRef({});
  const dragStateRef = useRef(null);
  const dragMovedRef = useRef(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [viewerError, setViewerError] = useState('');
  const [isRendering, setIsRendering] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [pageCount, setPageCount] = useState(0);
  const [pageFrames, setPageFrames] = useState([]);
  const [activeAudioPage, setActiveAudioPage] = useState(null);
  const [dragPositions, setDragPositions] = useState({});

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name?.toLowerCase().endsWith('.pdf')) {
      setViewerError('Select a valid PDF file.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setViewerError('PDF must be under 25 MB.');
      return;
    }
    setViewerError('');
    onUpload?.(file);
  };

  const viewerUrl = pdf?.url
    ? `${pdf.url}${pdf.url.includes('#') ? '&' : '#'}toolbar=0&navpanes=0&scrollbar=0&view=FitH`
    : null;
  const shouldRenderCanvas = renderMode === 'canvas';
  const zoomScale = zoom / 100;
  const resolvedAudioPages = useMemo(() => audioPages || {}, [audioPages]);

  const getAudioPosition = (pageNumber) => {
    const dragged = dragPositions[pageNumber];
    if (dragged) return dragged;
    return resolvedAudioPages[pageNumber]?.position || { x: 94, y: 6 };
  };

  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return undefined;

    const updateWidth = () => {
      const nextWidth = Math.floor(container.clientWidth || 0);
      setContainerWidth((current) => (current === nextWidth ? current : nextWidth));
    };

    updateWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(container);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [pdf?.url, shouldRenderCanvas]);

  useEffect(() => {
    if (!shouldRenderCanvas || !pdf?.url) {
      setPageCount(0);
      setPageFrames([]);
      return undefined;
    }

    let cancelled = false;

    const loadPdf = async () => {
      setViewerError('');
      setIsRendering(true);
      setPageCount(0);
      setPageFrames([]);
      canvasRefs.current = {};

      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (_) {}
        renderTaskRef.current = null;
      }

      if (pdfDocRef.current) {
        try {
          pdfDocRef.current.destroy();
        } catch (_) {}
        pdfDocRef.current = null;
      }

      try {
        const pdfjsLib = await ensurePdfJs();
        const loadingTask = pdfjsLib.getDocument(pdf.url);
        const pdfDoc = await loadingTask.promise;
        if (cancelled) {
          pdfDoc.destroy();
          return;
        }
        pdfDocRef.current = pdfDoc;
        setPageCount(pdfDoc.numPages);
      } catch (renderError) {
        if (!cancelled) {
          console.error('Failed to load PDF:', renderError);
          setViewerError(renderError?.message || 'Unable to render this PDF.');
          setIsRendering(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (_) {}
        renderTaskRef.current = null;
      }
      if (pdfDocRef.current) {
        try {
          pdfDocRef.current.destroy();
        } catch (_) {}
        pdfDocRef.current = null;
      }
    };
  }, [pdf?.url, shouldRenderCanvas]);

  useEffect(() => {
    if (!shouldRenderCanvas || !pdfDocRef.current || !containerWidth || !pageCount) {
      return undefined;
    }

    let cancelled = false;

    const renderPages = async () => {
      setViewerError('');
      setIsRendering(true);
      const pdfDoc = pdfDocRef.current;
      const nextFrames = [];

      try {
        for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
          const canvas = canvasRefs.current[pageNumber];
          if (!canvas) continue;
          const page = await pdfDoc.getPage(pageNumber);
          if (cancelled) return;

          const unscaledViewport = page.getViewport({ scale: 1 });
          const maxWidth = Math.min(containerWidth || 960, 960);
          const baseScale = maxWidth / unscaledViewport.width;
          const scale = Math.max(baseScale * zoomScale, 0.2);
          const viewport = page.getViewport({ scale });

          const context = canvas.getContext('2d');
          if (!context) {
            throw new Error('Canvas rendering is not available in this browser.');
          }

          const outputScale = window.devicePixelRatio || 1;
          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;
          canvas.style.maxWidth = '100%';
          canvas.style.display = 'block';
          canvas.style.margin = '0 auto';

          const renderContext = {
            canvasContext: context,
            viewport,
            transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null,
          };

          const renderTask = page.render(renderContext);
          renderTaskRef.current = renderTask;
          await renderTask.promise;
          nextFrames.push({ pageNumber, width: viewport.width, height: viewport.height });
        }

        if (!cancelled) {
          setPageFrames(nextFrames);
        }
      } catch (renderError) {
        if (!cancelled && renderError?.name !== 'RenderingCancelledException') {
          console.error('Failed to render shared PDF:', renderError);
          setViewerError(renderError?.message || 'Unable to render this PDF.');
        }
      } finally {
        if (!cancelled) {
          setIsRendering(false);
        }
      }
    };

    renderPages();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (_) {}
        renderTaskRef.current = null;
      }
    };
  }, [containerWidth, pageCount, shouldRenderCanvas, zoomScale]);

  useEffect(() => {
    if (!dragStateRef.current) return undefined;

    const handlePointerMove = (event) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const dx = Math.abs(event.clientX - dragState.startX);
      const dy = Math.abs(event.clientY - dragState.startY);
      if (dx > 3 || dy > 3) {
        dragMovedRef.current = true;
      }
      const x = Math.min(96, Math.max(4, ((event.clientX - dragState.rect.left) / dragState.rect.width) * 100));
      const y = Math.min(94, Math.max(4, ((event.clientY - dragState.rect.top) / dragState.rect.height) * 100));
      setDragPositions((current) => ({
        ...current,
        [dragState.pageNumber]: { x, y },
      }));
    };

    const handlePointerUp = () => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const nextPosition = dragPositions[dragState.pageNumber];
      if (dragMovedRef.current && nextPosition) {
        onMovePageAudio?.(dragState.pageNumber, nextPosition);
      }
      dragStateRef.current = null;
      setTimeout(() => {
        dragMovedRef.current = false;
      }, 0);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [dragPositions, onMovePageAudio]);

  const startDrag = (event, pageNumber) => {
    if (!canManageAudio) return;
    const pageEl = event.currentTarget.closest('[data-pdf-page-shell="true"]');
    if (!pageEl) return;
    dragMovedRef.current = false;
    dragStateRef.current = {
      pageNumber,
      rect: pageEl.getBoundingClientRect(),
      startX: event.clientX,
      startY: event.clientY,
    };
  };

  return (
    <div className="px-3 py-4 sm:px-6 sm:py-8">
      <div className="w-full space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">PDF Viewer</h2>
            <p className="text-sm text-gray-600">
              Upload, save, share, and reopen PDFs in the same document flow.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <label
                className={clsx(
                  'inline-flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white',
                  isUploading ? 'cursor-wait bg-sigma-400' : 'bg-sigma-600 hover:bg-sigma-700'
                )}
              >
                <FileUp className={clsx('h-4 w-4', isUploading && 'animate-pulse')} />
                {isUploading ? 'Uploading PDF...' : (pdf ? 'Replace PDF' : 'Upload PDF')}
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  disabled={isUploading}
                  onChange={handleFileChange}
                />
              </label>
            )}
            {canEdit && pdf && (
              <button
                type="button"
                onClick={onDelete}
                disabled={isUploading}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                Remove PDF
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {viewerError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {viewerError}
          </div>
        )}

        {isUploading && !pdf?.url ? (
          <div className="rounded-2xl border-2 border-dashed border-sigma-200 bg-white px-8 py-16 text-center">
            <FileUp className="mx-auto h-9 w-9 animate-pulse text-sigma-600" />
            <p className="mt-3 text-base font-medium text-gray-800">Uploading PDF...</p>
            <p className="mt-2 text-sm text-gray-500">Keep this page open while the document is uploaded securely.</p>
          </div>
        ) : !pdf?.url ? (
          <div className="rounded-2xl border-2 border-dashed border-gray-300 bg-white px-8 py-16 text-center">
            <p className="text-base font-medium text-gray-800">No PDF uploaded yet</p>
            <p className="mt-2 text-sm text-gray-500">
              Upload a PDF, then save this document to make it available in open and share flows.
            </p>
          </div>
        ) : (
          <div
            className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
            onContextMenu={(event) => event.preventDefault()}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-3 py-3 sm:px-4">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{pdf.name || 'Document.pdf'}</p>
                <p className="text-xs text-gray-500">
                  {typeof pdf.size === 'number' ? `${(pdf.size / (1024 * 1024)).toFixed(2)} MB` : 'PDF file'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white">
                  <button
                    type="button"
                    onClick={() => setZoom((current) => Math.max(50, current - 10))}
                    className="inline-flex h-9 w-9 items-center justify-center text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
                    title="Zoom out"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setZoom(100)}
                    className="min-w-[58px] border-x border-gray-200 px-2 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                    title="Reset zoom"
                  >
                    {zoom}%
                  </button>
                  <button
                    type="button"
                    onClick={() => setZoom((current) => Math.min(200, current + 10))}
                    className="inline-flex h-9 w-9 items-center justify-center text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
                    title="Zoom in"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
            {shouldRenderCanvas ? (
              <div className="bg-gray-100 px-2 py-3 sm:px-4 sm:py-5">
                {isRendering && (
                  <div className="mb-4 rounded-lg border border-sigma-100 bg-white px-4 py-3 text-sm text-sigma-700">
                    Rendering PDF pages...
                  </div>
                )}
                <div ref={canvasContainerRef} className="mx-auto space-y-4" style={{ maxWidth: '960px', width: '100%' }}>
                  {Array.from({ length: pageCount }, (_, index) => {
                    const pageNumber = index + 1;
                    const frame = pageFrames.find((item) => item.pageNumber === pageNumber);
                    const audio = resolvedAudioPages[pageNumber] || null;
                    const markerPosition = getAudioPosition(pageNumber);
                    const showMarker = !!audio || canManageAudio;

                    return (
                      <div
                        key={`pdf-page-${pageNumber}`}
                        data-pdf-page-shell="true"
                        className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                        style={{
                          width: frame?.width ? `${frame.width}px` : '100%',
                          maxWidth: '100%',
                          margin: '0 auto',
                        }}
                      >
                        <canvas ref={(node) => { canvasRefs.current[pageNumber] = node; }} />
                        <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-black/60 px-2 py-1 text-[11px] font-semibold text-white">
                          Page {pageNumber}
                        </div>
                        {showMarker && (
                          <div
                            className="absolute z-10"
                            style={{
                              left: `${markerPosition.x}%`,
                              top: `${markerPosition.y}%`,
                              transform: 'translate(-50%, -50%)',
                            }}
                          >
                            <button
                              type="button"
                              onPointerDown={(event) => startDrag(event, pageNumber)}
                              onClick={() => {
                                if (dragMovedRef.current) return;
                                if (audio) {
                                  setActiveAudioPage((current) => current === pageNumber ? null : pageNumber);
                                  return;
                                }
                                onAddPageAudio?.(pageNumber);
                              }}
                              className={clsx(
                                'flex h-11 w-11 items-center justify-center rounded-full border shadow-lg transition-colors',
                                audio
                                  ? 'border-sigma-200 bg-white text-sigma-700 hover:bg-sigma-50'
                                  : 'border-emerald-200 bg-emerald-600 text-white hover:bg-emerald-700'
                              )}
                              title={audio ? 'Open page audio' : 'Add page audio'}
                            >
                              {audio ? <Volume2 className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                            </button>

                            {audio && activeAudioPage === pageNumber && (
                              <div className="absolute right-0 top-14 z-20 w-[280px] max-w-[70vw]">
                                <DocumentNarrationPlayer
                                  narration={audio}
                                  title={`Page ${pageNumber} audio`}
                                  subtitle={audio.name || 'Audio available'}
                                  variant="overlay"
                                  onManage={canManageAudio ? () => onManagePageAudio?.(pageNumber) : undefined}
                                  onRemove={canManageAudio ? () => onRemovePageAudio?.(pageNumber) : undefined}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="overflow-auto bg-gray-100" style={{ height: `${embedViewportHeight}vh` }}>
                <div className="flex min-h-full min-w-full justify-center px-4 py-5">
                  <div
                    className="shrink-0"
                    style={{
                      width: `min(${documentBaseWidth}, 100%)`,
                      height: `${embedViewportHeight * zoomScale}vh`,
                    }}
                  >
                    <div
                      className="origin-top"
                      style={{
                        transform: `scale(${zoomScale})`,
                        transformOrigin: 'top center',
                      }}
                    >
                      <iframe
                        src={viewerUrl}
                        title={pdf.name || 'PDF document'}
                        className="block w-full border-0 bg-gray-100"
                        style={{ height: `${embedViewportHeight}vh` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfDocumentPanel;
